/**
 * SERIAL NUMBERS PAGE
 *
 * List and search serial numbers tracked in the warehouse.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  Text,
  BlockStack,
  InlineStack,
  Button,
  Badge,
  DataTable,
  Banner,
  EmptyState,
  Spinner,
  TextField,
  Select,
  Pagination,
  Link,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';

interface SerialNumber {
  id: number;
  serial_number: string;
  product_id: number;
  product_sku: string;
  product_title: string;
  batch_number: string | null;
  location_id: number | null;
  location_name: string | null;
  status: string;
  sold_to_order_id: number | null;
  order_number: string | null;
  received_at: string;
  sold_at: string | null;
}

interface SerialsResponse {
  serials: SerialNumber[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export default function SerialNumbers() {
  const navigate = useNavigate();
  const { fetchApi, loading, error } = useApi();
  const [serials, setSerials] = useState<SerialNumber[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const loadSerials = useCallback(async () => {
    const params = new URLSearchParams({
      page: String(page),
      limit: '50',
    });

    if (searchQuery) {
      params.set('search', searchQuery);
    }
    if (statusFilter !== 'all') {
      params.set('status', statusFilter);
    }

    const data = await fetchApi<SerialsResponse>(`/inventory/serials?${params}`);
    if (data) {
      setSerials(data.serials || []);
      setTotalCount(data.total || 0);
      setTotalPages(data.totalPages || 0);
    }
  }, [fetchApi, page, searchQuery, statusFilter]);

  useEffect(() => {
    loadSerials();
  }, [loadSerials]);

  const handleSearch = () => {
    setPage(1);
    loadSerials();
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'available':
        return <Badge tone="success">Available</Badge>;
      case 'allocated':
        return <Badge tone="info">Allocated</Badge>;
      case 'sold':
        return <Badge>Sold</Badge>;
      case 'returned':
        return <Badge tone="warning">Returned</Badge>;
      case 'written_off':
        return <Badge tone="critical">Written Off</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  if (loading && serials.length === 0) {
    return (
      <Page title="Serial Numbers">
        <Layout>
          <Layout.Section>
            <Card>
              <div style={{ padding: '2rem', textAlign: 'center' }}>
                <Spinner size="large" />
                <Text as="p" tone="subdued">Loading serial numbers...</Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title="Serial Numbers"
      subtitle={`${totalCount.toLocaleString()} serial numbers tracked`}
    >
      <Layout>
        {error && (
          <Layout.Section>
            <Banner tone="critical">{error}</Banner>
          </Layout.Section>
        )}

        {/* Search and Filters */}
        <Layout.Section>
          <Card>
            <InlineStack gap="400" blockAlign="end" wrap>
              <div style={{ flex: 1, minWidth: '200px' }}>
                <TextField
                  label="Search"
                  labelHidden
                  placeholder="Search by serial number, SKU, or product..."
                  value={searchQuery}
                  onChange={setSearchQuery}
                  autoComplete="off"
                  connectedRight={
                    <Button onClick={handleSearch} loading={loading}>
                      Search
                    </Button>
                  }
                />
              </div>
              <div style={{ width: '150px' }}>
                <Select
                  label="Status"
                  options={[
                    { label: 'All Status', value: 'all' },
                    { label: 'Available', value: 'available' },
                    { label: 'Allocated', value: 'allocated' },
                    { label: 'Sold', value: 'sold' },
                    { label: 'Returned', value: 'returned' },
                    { label: 'Written Off', value: 'written_off' },
                  ]}
                  value={statusFilter}
                  onChange={(val) => {
                    setStatusFilter(val);
                    setPage(1);
                  }}
                />
              </div>
            </InlineStack>
          </Card>
        </Layout.Section>

        {/* Serials Table */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              {serials.length > 0 ? (
                <>
                  <DataTable
                    columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
                    headings={['Serial Number', 'Product', 'SKU', 'Location', 'Status', 'Order', 'Actions']}
                    rows={serials.map((serial) => [
                      <Link
                        key={`serial-${serial.id}`}
                        onClick={() => navigate(`/serial-numbers/${encodeURIComponent(serial.serial_number)}`)}
                        monochrome
                        removeUnderline
                      >
                        <Text as="span" fontWeight="semibold">{serial.serial_number}</Text>
                      </Link>,
                      serial.product_title.length > 30
                        ? serial.product_title.substring(0, 30) + '...'
                        : serial.product_title,
                      serial.product_sku || '-',
                      serial.location_name || '-',
                      getStatusBadge(serial.status),
                      serial.order_number ? (
                        <Link
                          key={`order-${serial.id}`}
                          onClick={() => navigate(`/orders/${serial.sold_to_order_id}`)}
                          monochrome
                        >
                          {serial.order_number}
                        </Link>
                      ) : (
                        '-'
                      ),
                      <Button
                        key={`view-${serial.id}`}
                        size="slim"
                        onClick={() => navigate(`/serial-numbers/${encodeURIComponent(serial.serial_number)}`)}
                      >
                        View History
                      </Button>,
                    ])}
                  />

                  <InlineStack align="center" gap="400">
                    <Pagination
                      hasPrevious={page > 1}
                      hasNext={page < totalPages}
                      onPrevious={() => setPage(page - 1)}
                      onNext={() => setPage(page + 1)}
                    />
                    <Text as="span" tone="subdued">
                      Page {page} of {totalPages} ({totalCount.toLocaleString()} serials)
                    </Text>
                  </InlineStack>
                </>
              ) : (
                <EmptyState
                  heading="No serial numbers found"
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <p>
                    Serial numbers are tracked when receiving inventory with serial number tracking enabled.
                  </p>
                </EmptyState>
              )}
            </BlockStack>
          </Card>
        </Layout.Section>
      </Layout>
    </Page>
  );
}
